import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-800 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10";

export default function LeadFormCard({ mortgageType, title }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                mortgage_type: mortgageType,
                message: form.message.trim() || null,
            });
            setSent(true);
            setForm({ name: "", email: "", phone: "", message: "" });
            toast.success("¡Solicitud recibida! Te contactaremos en menos de 24h.");
        } catch (err) {
            toast.error("No se pudo enviar la solicitud. Inténtalo de nuevo.");
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <div
                data-testid="detail-lead-success"
                className="rounded-3xl bg-white p-8 sm:p-10 shadow-2xl shadow-black/30 text-center flex flex-col items-center justify-center min-h-[380px]"
            >
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500">
                    <CheckCircle2 size={34} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-[#0F172A]">
                    ¡Solicitud enviada!
                </h3>
                <p className="mt-3 text-slate-600">
                    Un asesor hipotecario revisará tu caso y te contactará en
                    menos de 24 horas laborables.
                </p>
                <button
                    data-testid="detail-lead-another"
                    onClick={() => setSent(false)}
                    className="mt-8 rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-[#1F3B5B] hover:border-[#1F3B5B] transition-colors"
                >
                    Enviar otra solicitud
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid="detail-lead-card"
            className="rounded-3xl bg-white p-7 sm:p-9 shadow-2xl shadow-black/30"
        >
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-600">
                Estudio gratuito · Sin compromiso
            </p>
            <h3 className="mt-2.5 font-display text-xl sm:text-2xl font-bold text-[#0F172A] text-balance">
                {title}
            </h3>
            <form
                data-testid="detail-lead-form"
                onSubmit={submit}
                className="mt-6 grid gap-4"
            >
                <input
                    data-testid="detail-lead-name"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nombre completo *"
                    className={inputCls}
                />
                <div className="grid sm:grid-cols-2 gap-4">
                    <input
                        data-testid="detail-lead-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="Email *"
                        className={inputCls}
                    />
                    <input
                        data-testid="detail-lead-phone"
                        required
                        type="tel"
                        minLength={6}
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="Teléfono *"
                        className={inputCls}
                    />
                </div>
                <textarea
                    data-testid="detail-lead-message"
                    rows={3}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Cuéntanos tu caso (opcional)"
                    className={`${inputCls} resize-none`}
                />
                <button
                    data-testid="detail-lead-submit"
                    type="submit"
                    disabled={sending}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-[#0B1A2B] font-bold px-6 py-4 text-base transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                >
                    {sending ? (
                        <>
                            <Loader2 size={18} className="animate-spin" />
                            Enviando...
                        </>
                    ) : (
                        "Solicitar estudio gratuito"
                    )}
                </button>
                <p className="text-xs text-slate-400 text-center">
                    Respuesta en menos de 24h. Nunca compartiremos tus datos.
                </p>
            </form>
        </div>
    );
}
